/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';

const user = JSON.parse(localStorage.getItem('nemt_user') || '{}');

export default function PaulaConsole() {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: "Hey Paula. What's going on? I can look up trips, check logs, fix data — whatever you need." },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [toolLog, setToolLog] = useState([]);
  const [showTools, setShowTools] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    const userMsg = { role: 'user', content: text };
    const updated = [...messages, userMsg];
    setMessages(updated);
    setLoading(true);
    setToolLog([]);

    try {
      const apiMessages = updated
        .filter((m, i) => i > 0 || m.role === 'user')
        .map(m => ({ role: m.role, content: m.content }));

      const res = await api.post('/paula-console', {
        messages: apiMessages,
        userEmail: user.email,
      });
      setMessages(prev => [...prev, { role: 'assistant', content: res.data.reply }]);
      if (res.data.toolLog) setToolLog(res.data.toolLog);
    } catch (e) {
      const errMsg = e.response?.status === 403
        ? 'This console is only available for Paula.'
        : 'Something went wrong. Try again.';
      setMessages(prev => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
      if (inputRef.current) inputRef.current.focus();
    }
  };

  if (user.email !== 'paula@commonwealthtransport.com') {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>-_-</div>
        <div style={{ fontSize: 14 }}>This page is only available for Paula.</div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)', maxWidth: 650, margin: '0 auto' }}>
      <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #e0e0db', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 500, margin: 0, color: '#185FA5' }}>Paula's Console</h1>
          <div style={{ fontSize: 11, color: '#999', marginTop: 2 }}>Claude with database + log access</div>
        </div>
        {toolLog.length > 0 && (
          <button onClick={() => setShowTools(!showTools)} style={{
            fontSize: 10, padding: '3px 8px', borderRadius: 10,
            background: '#f5f5f3', border: '0.5px solid #e0e0db', color: '#666', cursor: 'pointer',
          }}>
            {toolLog.length} tool{toolLog.length > 1 ? 's' : ''} used {showTools ? '(hide)' : '(show)'}
          </button>
        )}
      </div>

      {showTools && toolLog.length > 0 && (
        <div style={{ padding: '8px 16px', background: '#f9f9f8', borderBottom: '0.5px solid #e0e0db', maxHeight: 150, overflow: 'auto' }}>
          {toolLog.map((t, i) => (
            <div key={i} style={{ fontSize: 10, fontFamily: 'monospace', color: '#666', marginBottom: 4 }}>
              <span style={{ color: '#185FA5', fontWeight: 600 }}>{t.tool}</span>
              {t.input?.sql && <span> — {t.input.sql.substring(0, 80)}...</span>}
              {t.input?.pattern && <span> — grep "{t.input.pattern}"</span>}
              {t.result?.rowCount != null && <span style={{ color: '#1D9E75' }}> ({t.result.rowCount} rows)</span>}
              {t.result?.error && <span style={{ color: '#C62828' }}> ERROR: {t.result.error}</span>}
            </div>
          ))}
        </div>
      )}

      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>
        {messages.map((msg, i) => (
          <div key={i} style={{
            display: 'flex',
            justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
            marginBottom: 8,
          }}>
            <div style={{
              maxWidth: '85%',
              padding: '8px 12px',
              borderRadius: msg.role === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
              background: msg.role === 'user' ? '#185FA5' : '#f5f5f3',
              color: msg.role === 'user' ? 'white' : '#333',
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: 'pre-wrap',
            }}>
              {msg.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
            <div style={{ padding: '8px 12px', borderRadius: '14px 14px 14px 4px', background: '#f5f5f3', color: '#999', fontSize: 13 }}>
              Investigating...
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: '8px 12px', borderTop: '0.5px solid #e0e0db', background: 'white' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && send()}
            placeholder="What's broken? What needs fixing?"
            style={{
              flex: 1, fontSize: 14, padding: '10px 14px',
              border: '0.5px solid #e0e0db', borderRadius: 20, outline: 'none',
            }}
            autoFocus
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            style={{
              padding: '8px 16px', borderRadius: 20,
              background: input.trim() && !loading ? '#185FA5' : '#e0e0db',
              color: 'white', border: 'none', fontSize: 13, fontWeight: 600,
              cursor: input.trim() && !loading ? 'pointer' : 'default',
            }}
          >
            Send
          </button>
        </div>
        <div style={{ fontSize: 10, color: '#bbb', textAlign: 'center', marginTop: 4 }}>
          Has full database access. Can run queries and fix data.
        </div>
      </div>
    </div>
  );
}
